/**
 * Route mapping utility for multilingual URLs
 * Dutch routes are the English route under a /nl/ prefix. Slugs are shared
 * between the two languages, except for articles that carry a Dutch keyword
 * slug — those are translated through src/lib/articleSlugMapping.ts.
 */

import { getInternalArticleSlug, getLocalizedArticleSlug } from './articleSlugMapping';

export type Language = 'en' | 'nl';

// Base routes (same slugs for both languages)
const baseRoutes = [
  '/',
  '/services',
  '/cases',
  '/articles',
  '/about',
  '/contact',
  '/book',
  '/privacy-policy',
  '/terms-of-service',
];

/**
 * Get the localized route for a given language
 */
export function getLocalizedRoute(path: string, language: Language): string {
  // Remove trailing slash for consistency
  const normalizedPath = path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
  
  // Handle dynamic routes (services/:id, articles/:slug)
  if (normalizedPath.startsWith('/services/')) {
    const serviceId = normalizedPath.replace('/services/', '');
    return language === 'nl' ? `/nl/services/${serviceId}` : `/services/${serviceId}`;
  }
  
  if (normalizedPath.startsWith('/articles/')) {
    const articleSlugOrInternal = normalizedPath.replace('/articles/', '');
    const internalSlug = getInternalArticleSlug(articleSlugOrInternal);
    const localizedSlug = getLocalizedArticleSlug(internalSlug, language);
    return language === 'nl' ? `/nl/articles/${localizedSlug}` : `/articles/${localizedSlug}`;
  }
  
  // Handle /nl/services/ and /nl/articles/ (already localized)
  if (normalizedPath.startsWith('/nl/services/')) {
    const serviceId = normalizedPath.replace('/nl/services/', '');
    return language === 'nl' ? normalizedPath : `/services/${serviceId}`;
  }
  
  if (normalizedPath.startsWith('/nl/articles/')) {
    const articleSlugOrInternal = normalizedPath.replace('/nl/articles/', '');
    const internalSlug = getInternalArticleSlug(articleSlugOrInternal);
    const localizedSlug = getLocalizedArticleSlug(internalSlug, language);
    return language === 'nl' ? `/nl/articles/${localizedSlug}` : `/articles/${localizedSlug}`;
  }
  
  // If already has language prefix, extract the base path first
  const basePath = normalizedPath.startsWith('/nl/') 
    ? normalizedPath.replace('/nl', '')
    : normalizedPath;
  
  // For Dutch, add /nl prefix (using same English slugs)
  if (language === 'nl') {
    // For root path, return /nl, otherwise add /nl prefix
    if (basePath === '/') {
      return '/nl';
    }
    return `/nl${basePath}`;
  }
  
  // For English, return as-is
  return basePath;
}

/**
 * Get the base route (without language prefix)
 */
export function getBaseRoute(path: string): string {
  // Handle dynamic routes first
  if (path.startsWith('/nl/services/')) {
    const serviceId = path.replace('/nl/services/', '');
    return `/services/${serviceId}`;
  }
  
  if (path.startsWith('/nl/articles/')) {
    const articleSlugOrInternal = path.replace('/nl/articles/', '');
    const internalSlug = getInternalArticleSlug(articleSlugOrInternal);
    return `/articles/${getLocalizedArticleSlug(internalSlug, 'en')}`;
  }

  if (path.startsWith('/articles/')) {
    // Normalize to the English slug so alternates resolve from either language
    const articleSlugOrInternal = path.replace('/articles/', '');
    const internalSlug = getInternalArticleSlug(articleSlugOrInternal);
    return `/articles/${getLocalizedArticleSlug(internalSlug, 'en')}`;
  }
  
  // Remove /nl/ prefix if present (Dutch routes use same slugs as English)
  if (path.startsWith('/nl/')) {
    return path.replace('/nl', '');
  }
  
  if (path === '/nl') {
    return '/';
  }
  
  return path;
}

/**
 * Detect language from URL path
 */
export function detectLanguageFromPath(path: string): Language {
  return path.startsWith('/nl/') || path === '/nl' ? 'nl' : 'en';
}

/**
 * Get all routes for a given language (for sitemap generation)
 */
export function getAllRoutesForLanguage(language: Language): string[] {
  return baseRoutes.map(route => getLocalizedRoute(route, language));
}

/**
 * Get the alternate language URL for hreflang tags
 */
export function getAlternateLanguageUrl(currentPath: string, targetLanguage: Language): string {
  const baseRoute = getBaseRoute(currentPath);
  return getLocalizedRoute(baseRoute, targetLanguage);
}

/**
 * Check if a path is a valid route
 */
export function isValidRoute(path: string): boolean {
  const basePath = getBaseRoute(path);
  return baseRoutes.includes(basePath) || path.startsWith('/services/') || path.startsWith('/articles/') || path.startsWith('/nl/services/') || path.startsWith('/nl/articles/');
}


/** The service id of a service page path (`/services/<id>`, `/nl/services/<id>`), or null. */
export function getServiceIdFromPath(path: string): string | null {
  const m = path.match(/^\/(?:nl\/)?services\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}
